import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Trash2, ShieldOff, ShieldCheck, RefreshCw, Crown, FileDown } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Pager from "@/components/Pager";

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const PER = 20;
  const filtered = rows.filter((u) => {
    if (roleFilter !== "all" && u.role !== roleFilter) return false;
    if (statusFilter !== "all" && u.status !== statusFilter) return false;
    if (q && !(`${u.full_name || ""} ${u.email || ""}`.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });
  const pages = Math.max(1, Math.ceil(filtered.length / PER));
  const view = filtered.slice((page - 1) * PER, page * PER);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const toggleStatus = async (u) => {
    const next = u.status === "active" ? "suspended" : "active";
    try {
      await api.patch(`/admin/users/${u.id}/status`, { status: next });
      toast.success(`User ${next}`);
      setRows((rs) => rs.map((r) => (r.id === u.id ? { ...r, status: next } : r)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const setRole = async (u) => {
    const makeAdmin = u.role !== "admin";
    if (!confirm(makeAdmin
      ? `Make ${u.email} an admin? They will gain full access to the admin panel.`
      : `Revoke admin from ${u.email}? They will become a regular user.`)) return;
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role: makeAdmin ? "admin" : "user" });
      toast.success(makeAdmin ? "User promoted to admin" : "Admin access revoked");
      setRows((rs) => rs.map((r) => (r.id === u.id ? { ...r, role: makeAdmin ? "admin" : "user" } : r)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (u) => {    if (!confirm(`Delete ${u.email}? This is permanent.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      setRows((rs) => rs.filter((r) => r.id !== u.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const exportCsv = () => {
    const esc = (c) => `"${String(c ?? "").replace(/"/g, '""')}"`;
    const header = ["Full name", "Email", "Role", "Status", "Joined"].map(esc).join(",");
    const lines = filtered.map((u) => [u.full_name, u.email, u.role, u.status, (u.created_at || "").slice(0, 10)].map(esc).join(","));
    const blob = new Blob([[header, ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "users.csv";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-users">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <p className="overline mb-2">Admin · Users</p>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
            Members
          </h1>
          <p className="mt-2 text-sm text-slate-400">
            {rows.length} account{rows.length === 1 ? "" : "s"} on the platform{filtered.length !== rows.length ? ` · ${filtered.length} shown` : ""}.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportCsv} className="btn-edge-ghost text-xs" data-testid="admin-users-export">
            <FileDown size={14} /> Export CSV
          </button>
          <button
            onClick={load}
            className="btn-edge-ghost text-xs"
            data-testid="admin-users-refresh"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="admin-users-filters">
        <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search name or email…" data-testid="users-search"
          className="min-w-[200px] flex-1 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-white focus:border-emerald-500/40 focus:outline-none" />
        <select value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }} data-testid="users-role-filter"
          className="rounded-md border border-white/10 bg-[#0d2416] px-2 py-2 text-sm text-white">
          <option value="all">All roles</option>
          <option value="admin">Admin</option>
          <option value="user">User</option>
        </select>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} data-testid="users-status-filter"
          className="rounded-md border border-white/10 bg-[#0d2416] px-2 py-2 text-sm text-white">
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
        <span className="text-xs text-slate-500" data-testid="users-filter-count">{filtered.length} match{filtered.length === 1 ? "" : "es"}</span>
      </div>

      <div className="card-edge overflow-hidden">
        <div className="overflow-x-auto">
          <table
            className="min-w-full text-left text-sm"
            data-testid="admin-users-table"
          >
            <thead className="border-b border-white/5 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-5 py-3 font-medium">Full name</th>
                <th className="px-5 py-3 font-medium">Email</th>
                <th className="px-5 py-3 font-medium">Role</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Joined</th>
                <th className="px-5 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-slate-500">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-slate-500">
                    No users yet.
                  </td>
                </tr>
              )}
              {view.map((u, i) => (
                <motion.tr
                  key={u.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.03 }}
                  className="border-b border-white/5 last:border-0 hover:bg-emerald-500/[0.02]"
                >
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="grid h-8 w-8 place-items-center rounded-full bg-emerald-500/10 text-xs font-medium text-emerald-300">
                        {u.full_name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <span className="text-white">{u.full_name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-slate-300">{u.email}</td>
                  <td className="px-5 py-3.5">
                    <span
                      className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest ${
                        u.role === "admin"
                          ? "border-lime-400/25 bg-lime-400/10 text-lime-300"
                          : "border-white/10 bg-white/5 text-slate-300"
                      }`}
                    >
                      {u.role}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span
                      className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest ${
                        u.status === "active"
                          ? "border-emerald-400/25 bg-emerald-400/10 text-emerald-300"
                          : "border-amber-400/25 bg-amber-400/10 text-amber-300"
                      }`}
                    >
                      {u.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-xs text-slate-500 font-mono-edge">
                    {u.created_at?.slice(0, 10)}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setRole(u)}
                        disabled={u.id === me?.id}
                        data-testid={`admin-user-role-${u.id}`}
                        className={`rounded-lg border p-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                          u.role === "admin"
                            ? "border-lime-400/30 text-lime-300 hover:border-amber-400/50 hover:text-amber-300"
                            : "border-white/10 text-slate-300 hover:border-lime-400/50 hover:text-lime-300"
                        }`}
                        title={u.role === "admin" ? "Revoke admin" : "Make admin"}
                      >
                        <Crown size={14} />
                      </button>
                      <button
                        onClick={() => toggleStatus(u)}
                        disabled={u.id === me?.id}
                        data-testid={`admin-user-toggle-${u.id}`}
                        className="rounded-lg border border-white/10 p-1.5 text-slate-300 transition-colors hover:border-emerald-500/40 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                        title={u.status === "active" ? "Suspend" : "Activate"}
                      >
                        {u.status === "active" ? (
                          <ShieldOff size={14} />
                        ) : (
                          <ShieldCheck size={14} />
                        )}
                      </button>
                      <button
                        onClick={() => remove(u)}
                        disabled={u.id === me?.id}
                        data-testid={`admin-user-delete-${u.id}`}
                        className="rounded-lg border border-white/10 p-1.5 text-slate-300 transition-colors hover:border-red-500/40 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-40"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pager page={page} pages={pages} total={filtered.length} onPage={setPage} testId="admin-users-pager" />
      </div>
    </div>
  );
}
